import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, QrCode, Link2, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import KycModal from "./KycModal";
import { AutoTopupSettings } from "./AutoTopupSettings";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function UsageRing({ usage, t }) {
  if (!usage) return null;
  const pct = usage.total ? Math.max(0, Math.min(1, usage.remaining / usage.total)) : 0;
  const r = 34;
  const c = 2 * Math.PI * r;
  const fmt = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} ${t("misc.gb")}` : `${Math.round(mb)} MB`);
  return (
    <div data-testid="data-usage-meter" className="flex items-center gap-4">
      <svg width="84" height="84" viewBox="0 0 84 84" className="-rotate-90">
        <circle cx="42" cy="42" r={r} fill="none" stroke="var(--accent-sand)" strokeWidth="7" />
        <circle cx="42" cy="42" r={r} fill="none" stroke={pct < 0.2 ? "var(--accent-terracotta)" : "var(--status-active-green)"} strokeWidth="7"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div>
        <div className="font-display font-bold text-lg leading-tight">{fmt(usage.remaining)} <span className="text-[var(--text-muted)] font-normal text-sm">{t("acc.remaining")}</span></div>
        <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">{t("acc.of")} {fmt(usage.total)}</div>
      </div>
    </div>
  );
}

export function EsimCard({ sim }) {
  const { t, fmt, currency, lang } = useLanguage();
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [topOpen, setTopOpen] = useState(false);
  const [topups, setTopups] = useState(null);
  const [busyTop, setBusyTop] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const pendingTop = useRef(null);

  useEffect(() => {
    api.get(`/esims/${sim.iccid}/usage`).then(({ data }) => setUsage(data.data)).catch(() => {});
  }, [sim.iccid]);

  const shareLink = async () => {
    try {
      const { data } = await api.post(`/esims/${sim.iccid}/share-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success(t("acc.linkCopied"));
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const openTopup = () => {
    setTopOpen(true);
    if (topups === null) {
      api.get(`/esims/${sim.iccid}/topups`).then(({ data }) => setTopups(data.data || [])).catch(() => setTopups([]));
    }
  };

  const buyTopup = async (p) => {
    setBusyTop(p.package_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: p.package_id,
        iccid: sim.iccid,
        origin_url: window.location.origin,
        currency,
        lang,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 428) {
        pendingTop.current = p;
        setKycOpen(true);
      } else {
        toast.error(e.response?.data?.detail || t("misc.error"));
      }
      setBusyTop(null);
    }
  };

  const isExpired = usage && !usage.mock && (usage.expired === true || String(usage.status || "").toUpperCase() === "EXPIRED");

  return (
    <div data-testid={`esim-card-${sim.iccid}`} className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-8">
      <div className="flex justify-between items-start gap-4">
        <div>
          <div className="font-display font-bold text-2xl tracking-tight">{sim.package_title}</div>
          <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)] mt-1.5">{sim.destination}</div>
        </div>
        <div className="flex gap-2">
          {sim.mock && (
            <span className="rounded-full border border-[var(--accent-gold)] px-3 py-1 font-mono-t text-[9px] uppercase tracking-[0.25em] text-[var(--accent-gold)]">{t("acc.demo")}</span>
          )}
          {isExpired ? (
            usage.topupable === "grace" ? (
              <span data-testid={`esim-grace-${sim.iccid}`} className="rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1 font-mono-t text-[9px] uppercase tracking-[0.25em]">{t("acc.topupGrace")}</span>
            ) : (
              <span data-testid={`esim-notopup-${sim.iccid}`} className="rounded-full bg-slate-100 border border-slate-200 text-slate-500 px-3 py-1 font-mono-t text-[9px] uppercase tracking-[0.25em]">{t("acc.topupNo")}</span>
            )
          ) : (
            <span className="rounded-full bg-[var(--status-active-green)]/10 text-[var(--status-active-green)] px-3 py-1 font-mono-t text-[9px] uppercase tracking-[0.25em]">{t("acc.active")}</span>
          )}
        </div>
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-6">
        <UsageRing usage={usage} t={t} />
        <div className="flex flex-wrap gap-3">
          <button data-testid={`esim-share-button-${sim.iccid}`} onClick={shareLink}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] px-6 py-2.5 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
            <Link2 className="w-4 h-4" /> {t("acc.share")}
          </button>
          <button data-testid={`topup-button-${sim.iccid}`} onClick={openTopup}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] px-6 py-2.5 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:border-[var(--accent-terracotta)] hover:text-[var(--accent-terracotta)] transition-colors">
            <Zap className="w-4 h-4" /> {t("acc.topup")}
          </button>
          <button data-testid={`esim-install-button-${sim.iccid}`} onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-2.5 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
            <QrCode className="w-4 h-4" /> {t("acc.install")}
          </button>
        </div>
      </div>
      {!sim.mock && <AutoTopupSettings sim={sim} />}
      {sim.mock && <p className="mt-6 text-xs text-[var(--text-muted)]">{t("acc.mockNote")}</p>}

      <Dialog open={topOpen} onOpenChange={setTopOpen}>
        <DialogContent data-testid={`topup-dialog-${sim.iccid}`} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("topup.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--text-secondary)]">{t("topup.sub")}</p>
          <div className="mt-4 space-y-3">
            {topups === null ? (
              <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--accent-terracotta)]" /></div>
            ) : topups.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--text-muted)]">{t("cat.none")}</p>
            ) : (
              topups.map((p) => (
                <div key={p.package_id} data-testid={`topup-option-${p.package_id}`}
                  className="flex items-center justify-between rounded-2xl border border-[var(--border-subtle)] px-5 py-4 hover:border-[var(--accent-terracotta)] transition-colors">
                  <div>
                    <div className="font-display font-bold">{p.is_unlimited ? t("dest.unlimited") : p.data}</div>
                    {p.validity && <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mt-0.5">{p.validity} {t("dest.days")}</div>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono-t text-sm text-[var(--accent-terracotta)]">{fmt(p.price)}</span>
                    <button data-testid={`topup-buy-${p.package_id}`} onClick={() => buyTopup(p)} disabled={busyTop === p.package_id}
                      className="rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-5 py-2 font-mono-t text-[10px] uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors disabled:opacity-60 flex items-center gap-2">
                      {busyTop === p.package_id && <Loader2 className="w-3 h-3 animate-spin" />}
                      {t("dest.buy")}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`esim-install-dialog-${sim.iccid}`} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("acc.install")} — {sim.package_title}</DialogTitle>
          </DialogHeader>
          <div className="text-center">
            {sim.qrcode && (
              <div className="inline-block rounded-2xl border border-[var(--border-strong)] p-4 bg-white">
                <QRCodeSVG value={sim.qrcode} size={200} />
              </div>
            )}
            <div className="mt-6 text-left space-y-4">
              <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">{t("acc.manual")}</div>
              <div>
                <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">{t("acc.smdp")}</div>
                <div className="font-mono-t text-sm break-all mt-1">{sim.lpa}</div>
              </div>
              <div>
                <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">{t("acc.code")}</div>
                <div className="font-mono-t text-sm break-all mt-1">{sim.matching_id}</div>
              </div>
              {sim.direct_apple_installation_url && (
                <a data-testid={`esim-apple-install-${sim.iccid}`} href={sim.direct_apple_installation_url}
                  className="block text-center rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-3 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
                  iOS 17.4+ Direct Install
                </a>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <KycModal open={kycOpen} onOpenChange={setKycOpen} onDone={() => { const p = pendingTop.current; if (p) buyTopup(p); }} />
    </div>
  );
}
